import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import Video, { type VideoProps } from './Video';

export type LazyVideoProps = VideoProps & {
  fallbackSrcs?: string[];
  /** Preserve the default error dimming unless the caller provides its own fallback. */
  dimOnError?: boolean;
  eager?: boolean;
  /** Keep the player mounted after its first decoded frame, even beyond the near margin. */
  retainAfterLoaded?: boolean;
  rootMargin?: string;
  threshold?: number;
  onInViewChange?: (inView: boolean) => void;
};

export default function LazyVideo({
  src,
  fallbackSrcs = [],
  dimOnError = true,
  eager = false,
  retainAfterLoaded = false,
  className = '',
  mode = 'autoplay',
  contained = false,
  onLoadedData,
  onError,
  rootMargin = '200px',
  threshold = 0.1,
  onInViewChange,
  ...videoProps
}: LazyVideoProps) {
  const { ref: inViewRef, inView } = useInView({
    rootMargin,
    threshold,
    skip: eager,
    onChange: onInViewChange,
  });
  const [currentSrcIndex, setCurrentSrcIndex] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [retainedSrc, setRetainedSrc] = useState<string | null>(null);

  const allSources = [src, ...fallbackSrcs];
  const currentSrc = allSources[currentSrcIndex];
  const sourceKey = videoProps.sources
    ?.map(({ src: sourceSrc, type }) => `${sourceSrc}:${type}`)
    .join('|');

  // Reset to first source when src prop changes
  useEffect(() => {
    setCurrentSrcIndex(0);
    setHasError(false);
    setRetainedSrc(null);
  }, [src]);

  const handleError: NonNullable<VideoProps['onError']> = (details) => {
    setRetainedSrc(null);
    if (currentSrcIndex < allSources.length - 1) {
      setCurrentSrcIndex((i) => i + 1);
    } else {
      setHasError(true);
    }
    onError?.(details);
  };

  return (
    <div
      ref={inViewRef}
      className={`${className} ${hasError && dimOnError ? 'opacity-50' : ''}`}
    >
      {(eager || inView || (retainAfterLoaded && retainedSrc === src)) && (
        <Video
          key={
            retainAfterLoaded || videoProps.startTime
              ? `${currentSrc}:${sourceKey ?? ''}`
              : undefined
          }
          {...videoProps}
          src={currentSrc}
          mode={mode}
          contained={contained}
          className="h-full w-full rounded-[inherit]"
          onLoadedData={() => {
            if (retainAfterLoaded) setRetainedSrc(src);
            onLoadedData?.();
          }}
          onError={handleError}
        />
      )}
    </div>
  );
}
